import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import FormField from "../components/FormField";
import { useEffect } from "react";
import { loginConfig as defaultConfig } from "../config/loginConfig";
import axiosInstance from "../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { loginWithToken } from "../utils/authHelpers";

const Login = ({ config = defaultConfig }) => {
  const [formData, setFormData] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleFieldChange = (name, value) => {
    let updatedValue = value;
    if (name === "mobileno") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const userId = params.get("user_id");
    const hospitalId = params.get("hospital_id");

    if (token) {
      loginWithToken({ token, role: "hospital", userId, user: { id: userId } });
      login({ token, role: "hospital", users: { id: userId }, userId });
      localStorage.setItem("hospital_id", hospitalId ?? "");
      navigate("/settings/profile", { replace: true });
    }
  }, []);

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      setErrorMessage("");
      const response = await axiosInstance.post(`accounts/api/login/`, formData);

      const token = response?.data?.token;
      const user = response?.data?.user;
      const role = user?.staff_role?.role_name;
      const userId = user?.id;
      const isSuspended = user?.is_suspended;

      if (!token || !role) {
        console.error("Invalid login response");
        return;
      }

      if (isSuspended) {
        alert("Your account is suspended. Please contact admin.");
        return;
      }

      loginWithToken({ token, role, userId, user });
      login({ token, role, users: user, userId });

      if (role === "Admin") navigate("/worklist/myworklist");
      else if (role === "carepreneur") navigate("/worklist/carepreneur");
      else if (role === "hospital") navigate("/settings/profile");
      else navigate("/");
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            p: 4,
          }}
        >
          {/* Logo / Brand */}
          <Box textAlign="center" mb={3}>
            
            <Typography variant="h5" fontWeight="bold" color="#1a1a1a">
              {config.sections[0].title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Sign in to your account
            </Typography>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
              {errorMessage}
            </Alert>
          )}

          {/* Form */}
          {config.sections.map((section) => (
            <Box key={section.title}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {section.fields.map((field) => (
                    <Box key={field.name}>
                      <FormField
                        field={field}
                        value={formData[field.name] || ""}
                        onChange={handleFieldChange}
                      />
                    </Box>
                  ))}

                  <Box textAlign="right">
                    <Typography
                      variant="body2"
                      color="#7C3996"
                      sx={{ cursor: "pointer", fontWeight: 500 }}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      py: 1.5,
                      backgroundColor: "#7C3996",
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "15px",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#9b59b6" },
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>
              </form>
            </Box>
          ))}

          <Box display="flex" alignItems="center" gap={1} mt={3}>
            <Divider sx={{ flex: 1, borderColor: "#eee" }} />
            <Typography variant="body2" color="text.secondary">OR</Typography>
            <Divider sx={{ flex: 1, borderColor: "#eee" }} />
          </Box>

          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              Need an account?{" "}
              <Typography
                component="span"
                sx={{ color: "#7C3996", cursor: "pointer", fontWeight: "bold" }}
              >
                Sign Up
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export { Login };